import type { ModuleMeta } from '../../core/types';
import { CI_CAPABILITIES } from './capabilities';
import type { CiFieldKey } from './fields';

function ciBinding<K extends (typeof CI_CAPABILITIES)[number]['key']>(key: K) {
  const binding = CI_CAPABILITIES.find((item) => item.key === key);
  if (!binding) {
    throw new Error(`CI capability binding not found: ${key}`);
  }
  return binding;
}

export const CI_MODULES = [
  {
    key: 'baseInfo',
    title: '基本信息',
    capabilities: [ciBinding('base-info')],
    fields: ['base.name', 'base.description', 'base.workspace', 'base.namespace', 'base.datacenter'],
    /** 与 presetConfig / CI_SKU_MODULE_META 一致（表单序列化路径） */
    presetCheckFields: ['name'],
  },
  {
    key: 'network',
    title: '网络设置',
    capabilities: [ciBinding('network')],
    fields: ['network.vpc', 'network.subnet', 'network.securitygroup'],
    presetCheckFields: ['resource.network.vpc.name'],
  },
  {
    key: 'systemSetting',
    title: '系统配置',
    capabilities: [ciBinding('run-policy'), ciBinding('image'), ciBinding('system-disk-size')],
    fields: ['runPolicy.strategy', 'image.image', 'systemDiskSize'],
    presetCheckFields: ['basic.image.key', 'systemDiskSize'],
    resetFieldPaths: [
      'runStrategy',
      'basic.image.key',
      'basic.image.url',
      'basic.image.name',
      'basic.image.imageName',
      'systemDiskSize',
      'systemDiskPricing',
    ],
  },
  {
    key: 'resource',
    title: '资源配置',
    capabilities: [ciBinding('compute')],
    fields: ['system.architecture', 'compute.cpu', 'compute.memory', 'compute.gpu', 'compute.bandwidth'],
    presetCheckFields: ['resource.cpu', 'resource.memory', 'resource.gpu', 'resource.network.Bandwidth'],
    resetFieldPaths: [
      'system.architecture',
      'resource.cpu',
      'resource.memory',
      'resource.gpu',
      'resource.cpuPricing',
      'resource.memoryPricing',
      'resource.gpuPricing',
      'resource.network.Bandwidth',
      'resource.network.bandwidthPricing',
      '_containerDeploySharedMemory',
      '_sharedMemoryEnabled',
      'network.nics',
      '_nicEnabled',
    ],
  },
  {
    key: 'startupCommandArgs',
    title: '启动命令和参数',
    capabilities: [ciBinding('startup-command-args')],
    fields: ['startupCommandArgs.command', 'startupCommandArgs.list'],
    presetCheckFields: ['basic.image.command', 'basic.image.startupArgs', 'basic.image.args'],
    resetFieldPaths: ['basic.image.command', 'basic.image.startupArgs', 'basic.image.args'],
  },
  {
    key: 'envVars',
    title: '环境变量',
    capabilities: [ciBinding('env-vars')],
    fields: ['envVars.list'],
    presetCheckFields: ['basic.image.env'],
    resetFieldPaths: ['basic.image.env'],
  },
  {
    key: 'service',
    title: '服务配置',
    capabilities: [ciBinding('service')],
    fields: ['service.list'],
    presetCheckFields: ['resource.network.services'],
    resetFieldPaths: ['resource.network.services'],
  },
  {
    key: 'storage',
    title: '存储配置',
    capabilities: [ciBinding('container-storage')],
    fields: ['containerStorage.list'],
    presetCheckFields: ['volumes'],
    resetFieldPaths: ['volumes'],
  },
  {
    key: 'dns',
    title: 'DNS配置',
    capabilities: [ciBinding('dns')],
    fields: ['network.dns.nameservers', 'network.dns.searches', 'network.dns.options'],
    presetCheckFields: [
      'resource.network.dnsConfig.nameservers',
      'resource.network.dnsConfig.searches',
      'resource.network.dnsConfig.options',
    ],
    resetFieldPaths: ['resource.network.dnsConfig'],
  },
] as const satisfies readonly ModuleMeta<CiFieldKey>[];

export const CI_MODULE_MAP = Object.fromEntries(CI_MODULES.map((item) => [item.key, item]));
